import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useStep } from '@/hooks/wizard/useStep';
import {
    updateRequest,
    updateMeta,
} from '@/actions/wizard/RequestWizardActions';
import Step from '@/views/Wizards/RequestWizard/Wrappers/Step';
import RequestWizardLoading from '@/views/Wizards/RequestWizard/States/RequestWizardLoading';
import LocationInput from '@/elements/inputs/LocationInput';
import useValidation from '@/hooks/wizard/useValidation';
import { getChefsCount } from '@/actions/wizard/RequestWizardActions';
import { debounce } from 'lodash';
import { useCountries } from '@/hooks/useCountries';

export default function LocationStep() {
    const dispatch = useDispatch();
    const { request, status, options, meta } = useSelector(
        (state) => state.requestWizard,
    );
    const { completeStep, stepError } = useValidation();
    const { activeStep } = useStep();
    const countries = useCountries();

    const prompt = (prompt) => dispatch(updateMeta({ activePrompt: prompt }));

    useEffect(() => {
        // Enforces chefs count prompt to show when location is valid
        if (
            activeStep.isComplete &&
            options.chefsCount > 0 &&
            request.country === 'GB'
        ) {
            prompt('chefs-count');
        }
    }, [options.chefsCount, activeStep.isComplete]);

    const handleLocationChange = (postcode, country) => {
        dispatch(updateRequest('postcode', postcode));
        if (country !== request.country) {
            dispatch(updateRequest('country', country));
            const currencySign =
                countries[country]?.currency_sign || '£';
            dispatch(updateMeta('currencySign', currencySign));
        }
    };

    const delayChefsCount = useCallback(
        debounce(() => dispatch(getChefsCount()), 300),
        [],
    );

    const delayValidation = useCallback(
        debounce((isValidLocation, isInternational, error) => {
            delayChefsCount.cancel();
            if (isInternational) {
                completeStep(false);
                prompt('intl-location');
            } else if (!isValidLocation) {
                prompt('none');
                stepError(error);
            } else {
                delayChefsCount();
                completeStep();
            }
        }, 300),
        [],
    );

    const handleValidationChange = ({
        isValidLocation,
        isInternational,
        error,
    }) => {
        delayValidation.cancel();
        delayValidation(isValidLocation, isInternational, error);
    };

    if (status.loading) {
        return <RequestWizardLoading />;
    }

    return (
        <Step size="md">
            <h4 className="step-title">Where?</h4>
            <p className="step-info text-wrap">
                {request.country === 'US'
                    ? 'Enter your zip code or city. We have chefs in all states.'
                    : request.country === 'GB'
                        ? 'Enter your postcode or city. Available UK-wide.'
                        : 'Enter your postal code or city.'}
            </p>
            <LocationInput
                value={request.postcode}
                country={request.country}
                onChange={handleLocationChange}
                onValidationChange={handleValidationChange}
            />
        </Step>
    );
}
